import { useState } from "react";
import api from "../../api/axios";
import AnimatedSection from "../common/AnimatedSection";

const Form = () => {
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // PHONE VALIDATION
    if (name === "phone") {

      if (/[^0-9]/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          phone: "Phone number must contain only numbers",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          phone: "",
        }));
      }

      setForm({
        ...form,
        [name]: value.replace(/[^0-9]/g, ""),
      });

      return;
    }

    // NAME VALIDATION
    if (name === "full_name") {

      // allow only letters and spaces
      if (/[^a-zA-Z\s]/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          full_name: "Name must contain only letters",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          full_name: "",
        }));
      }

      // prevent numbers/symbols from storing
      setForm({
        ...form,
        [name]: value.replace(/[^a-zA-Z\s]/g, ""),
      });

      return;
    }

    // OTHER FIELDS
    setForm({
      ...form,
      [name]: value,
    });

    setErrors({
      ...errors,
      [name]: "",
    });
  };

  // VALIDATION FUNCTION
  const validate = () => {
    const newErrors = {};

    // Name validation
    if (!form.full_name.trim()) {
      newErrors.full_name = "Name is required";
    } else if (form.full_name.trim().length < 3) {
      newErrors.full_name = "Name must be at least 3 characters";
    }

    // Phone validation
    const phoneRegex = /^[0-9]{10}$/;

    if (!form.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!phoneRegex.test(form.phone)) {
      newErrors.phone = "Phone number must be 10 digits";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(form.email)) {
      newErrors.email = "Enter a valid email address";
    }

    // Message validation
    if (!form.message.trim()) {
      newErrors.message = "Message is required";
    } else if (form.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    const validationErrors = validate();

    // stop if errors exist
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setError(null);
    setSubmitted(false);

    try {
      const res = await api.post("/contact/submit", form);

      console.log("API RESPONSE:", res.data);

      if (res.status === 200 && res.data?.status === true) {
        setSubmitted(true);

        setForm({
          full_name: "",
          phone: "",
          email: "",
          message: "",
        });

        setTimeout(() => setSubmitted(false), 3000);
      } else {
        setError(res.data?.message || "Failed to send message");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatedSection delay={0.2}>
      <div className="relative">
        <form onSubmit={handleSubmit} className="space-y-5">
          
          {/* Name */}
          <div>
            <input
              type="text"
              name="full_name"
              value={form.full_name}
              onChange={handleChange}
              placeholder="Your name"
              className={`w-full px-4 py-3.5 rounded-lg border outline-none transition ${
                errors.full_name
                  ? "border-red-500"
                  : "border-dark/10 focus:border-primary"
              }`}
            />

            {errors.full_name && (
              <p className="text-red-500 text-xs mt-1">
                {errors.full_name}
              </p>
            )}
          </div>

          {/* Phone */}
          <div>
            <input
              type="tel"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="9812345678"
              className={`w-full px-4 py-3.5 rounded-lg border outline-none transition ${
                errors.phone
                  ? "border-red-500"
                  : "border-dark/10 focus:border-primary"
              }`}
            />

            {errors.phone && (
              <p className="text-red-500 text-xs mt-1">
                {errors.phone}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="you@example.com"
              className={`w-full px-4 py-3.5 rounded-lg border outline-none transition ${
                errors.email
                  ? "border-red-500"
                  : "border-dark/10 focus:border-primary"
              }`}
            />

            {errors.email && (
              <p className="text-red-500 text-xs mt-1">
                {errors.email}
              </p>
            )}
          </div>

          {/* Message */}
          <div>
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder="Your message..."
              rows={5}
              className={`w-full px-4 py-3.5 rounded-lg border outline-none transition resize-none ${
                errors.message
                  ? "border-red-500"
                  : "border-dark/10 focus:border-primary"
              }`}
            />

            {errors.message && (
              <p className="text-red-500 text-xs mt-1">
                {errors.message}
              </p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full px-8 py-4 bg-primary text-white font-semibold rounded-lg hover:opacity-90 transition disabled:opacity-60"
          >
            {loading ? "Sending..." : "Send Message"}
          </button>
        </form>

        {/* API Error */}
        {error && (
          <div className="mt-4 text-red-500 text-sm font-medium">
            {error}
          </div>
        )}

        {/* Success Message */}
        {submitted && (
          <div className="absolute -bottom-20 right-0 px-5 py-3 text-white bg-green-600 text-sm font-medium rounded-lg shadow-lg">
            Message sent successfully!
          </div>
        )}
      </div>
    </AnimatedSection>
  );
};

export default Form;